import React, { useState } from "react";
import PropTypes from "prop-types";
import styles from "./CardList.module.css";
import CardItem from "../CardItem";
import CardModal from "../CardModal";
import Modal from "../Modal";
import WarningPrimaryModal from "components/WarningPrimaryModal";
import Button from "../Button";
import { useAccount } from "utils/AccountInfoContext";
import { useTranslation } from "react-i18next";

export function CardList({ cards, setPrimary, onDelete, onUpdate }) {
  const {t} = useTranslation();
  const initialPrimaryCard = cards.find((card) => card.isPrimary)?.id || null;
  const [primaryCard, setPrimaryCard] = useState(initialPrimaryCard);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { billingData } = useAccount();
  const [showBillingWarningModal, setShowBillingWarningModal] = useState(false);
  const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);

  const handleEdit = (card) => {
    if (billingData === true) {
      setShowBillingWarningModal(true);
    } else {
      setSelectedCard(card);
      setOpenModal(true);
    }
  };

  const handleOnClose = () => {
    setSelectedCard(null);
    setOpenModal(false);
  };

  const handleDelete = (index) => {
    const cardToDelete = cards[index];
    if (cardToDelete.isPrimary) {
      setShowDeleteWarningModal(true);
    } else {
      setShowDeleteConfirmation(true);
      setSelectedCard(cardToDelete);
    }
  };

  const confirmDelete = () => {
    if (selectedCard) {
      const cardToDeleteId = selectedCard.id;
      const updatedCards = cards.filter((card) => card.id !== cardToDeleteId);

      onDelete(cardToDeleteId);

      setShowDeleteConfirmation(false);
      setSelectedCard(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
    setSelectedCard(null);
  };

  const handleBillingWarningClose = () => {
    setShowBillingWarningModal(false);
  };

  const handleWarningModalClose = () => {
    setShowDeleteWarningModal(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {cards.map((item, index) => (
          <div className={styles.cardItem} key={item.id}>
            <CardItem
              cardInfo={item}
              primary={item.isPrimary}
              setPrimary={() => setPrimary(item.id)}
              onEdit={() => handleEdit(item)}
              onDelete={() => handleDelete(index)}
            />
          </div>
        ))}
      </div>

      {selectedCard && (
        <CardModal
          isOpen={openModal}
          onClose={handleOnClose}
          card={selectedCard}
          isEditing={true}
          onValidate={onUpdate}
        />
      )}

      {showDeleteConfirmation && selectedCard && (
        <Modal isOpen={showDeleteConfirmation}>
          <div className={styles.deleteContent}>
            <p className={styles.deleteHeader}>
              {t("Are you sure you want to delete this card")}
            </p>
            <p className={styles.deleteBody}>
              {selectedCard.nickName} {t("Ending in")} {selectedCard.lastFour}
            </p>
            <div className={styles.buttonContainer}>
              <Button buttonText={t("Yes")} onClick={confirmDelete} />
              <Button buttonText={t("No")} onClick={cancelDelete} />
            </div>
          </div>
        </Modal>
      )}

      {showBillingWarningModal && (
        <WarningPrimaryModal
          isOpen={showBillingWarningModal}
          title={"Primary Card Change Restricted"}
          description={
            t("The ability to change your card is disabled")
          }
          onClick={handleBillingWarningClose}
        />
      )}

      {showDeleteWarningModal && (
        <WarningPrimaryModal
          isOpen={showDeleteWarningModal}
          title={"You are required to have a primary card on file"}
          description={
            t("Please add a new card or select card")
          }
          onClick={handleWarningModalClose}
        />
      )}
    </div>
  );
}

CardList.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.object),
  setPrimary: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default CardList;
